// In-page sync triggers. This is the primary sync driver everywhere (iOS
// Safari has no Background Sync API); the service worker 'sync' event is a
// progressive enhancement on Chromium registered here after each kick.
import { runSync } from "@/lib/syncEngine";
import { db } from "@/lib/db";
import { isStorageDegraded } from "@/lib/storageCapability";

const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — major reduction in background roundtrips on cellular
const DEBOUNCE_MS = 10_000;

let lastRunAt = 0;
let running = false;

type SyncRequestOptions = {
  force?: boolean;
  followUp?: boolean;
};

async function registerBackgroundSync(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.ready;
    const sync = (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }).sync;
    await sync?.register("punch-sync");
  } catch {
    // Background Sync unsupported (iOS Safari) or registration denied — the
    // in-page triggers below remain the driver.
  }
}

/**
 * Kick a sync run. Debounced for automatic triggers; `force` (manual "Sync
 * now") bypasses both the debounce and per-punch backoff gates.
 */
export function requestSync(options?: SyncRequestOptions): void {
  // Private / broken IDB: no durable outbox to drain — skip entirely.
  if (isStorageDegraded()) return;

  const now = Date.now();
  if (!options?.force && (running || now - lastRunAt < DEBOUNCE_MS)) return;
  lastRunAt = now;
  running = true;
  void runSync({ ignoreBackoff: options?.force })
    .catch(() => undefined)
    .finally(() => {
      running = false;
    });
  void registerBackgroundSync();

  // For forced kicks (manual sync, login resume), schedule a
  // single short follow-up. This helps races with the 60s interval and
  // lock acquisition without causing a burst of /auth/me + sync attempts.
  if (options?.force && !options.followUp) {
    setTimeout(() => requestSync({ force: true, followUp: true }), 800);
  }
}

async function hasLocalPendingWork(): Promise<boolean> {
  if (isStorageDegraded()) return false;
  try {
    // Cheap local check — avoids waking radio for an empty outbox on the 5min tick.
    const [p, n] = await Promise.all([
      db.outbox.where("status").anyOf(["pending", "syncing"]).count(),
      db.fieldNotesOutbox.where("status").anyOf(["pending", "syncing"]).count(),
    ]);
    return (p + n) > 0;
  } catch {
    // If IDB is weird, err on the side of running the sync (it is cheap when empty).
    return true;
  }
}

/** Wire up all automatic triggers. Returns a cleanup function. */
export function startSyncScheduler(): () => void {
  const onOnline = () => requestSync();
  const onVisible = () => {
    if (document.visibilityState === "visible") requestSync();
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
  const interval = window.setInterval(async () => {
    if (await hasLocalPendingWork()) {
      requestSync();
    }
  }, SYNC_INTERVAL_MS);
  // Initial kick is always useful (resume after login etc.)
  requestSync();
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisible);
    window.clearInterval(interval);
  };
}
